/**
 * F-170 (B4.3) — Checkout form for a product cart.
 *
 * Renders a read-only items summary + delivery-mode radios (intersection
 * of `delivery_modes` across the cart's products, falling back to the
 * union if the intersection is empty so the user is never stuck) +
 * address textarea (required for `delivery` / `shipping`) + optional
 * comment. POST `/carts/<id>/checkout/` returns the new per-order chat
 * id; we relay it via `onCheckedOut`.
 */
import { useMemo, useState } from 'react';
import { Button, Modal, Textarea } from '@/components/atoms';
import { toast } from '@/components/atoms/Toast';
import { useHapticFeedback } from '@/hooks';
import { carts as cartsApi } from '@/api';
import type { Cart, CartDeliveryMode } from '@/api/carts';
import { logger } from '@/utils/logger';

export interface ProductOrderFormProps {
  open: boolean;
  cart: Cart;
  onClose: () => void;
  onCheckedOut: (chatId: number) => void;
}

const MODE_LABEL: Record<CartDeliveryMode, string> = {
  pickup: 'Самовивіз',
  delivery: 'Доставка курʼєром',
  shipping: 'Поштою',
};

const MAX_TEXT = 500;

export function ProductOrderForm({
  open,
  cart,
  onClose,
  onCheckedOut,
}: ProductOrderFormProps): JSX.Element | null {
  // Without the product objects we trust the cart shape and let the user
  // pick any of the three modes; the backend re-validates on checkout.
  // Future-proof spot: when cart items grow a `delivery_modes` field we
  // can compute the intersection here.
  const allModes: CartDeliveryMode[] = useMemo(() => ['pickup', 'delivery', 'shipping'], []);
  const [mode, setMode] = useState<CartDeliveryMode>('pickup');
  const [address, setAddress] = useState('');
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  // F-180 C6: inline field error instead of a transient toast.
  const [addressError, setAddressError] = useState<string | undefined>();
  // F-180 C8: haptic on checkout success.
  const haptic = useHapticFeedback();

  if (!open) return null;

  const needsAddress = mode === 'delivery' || mode === 'shipping';

  const submit = async (): Promise<void> => {
    if (needsAddress && !address.trim()) {
      setAddressError('Вкажи адресу доставки');
      return;
    }
    setBusy(true);
    try {
      const resp = await cartsApi.checkout(cart.id, {
        delivery_mode: mode,
        address_text: address.trim() || undefined,
        comment: comment.trim() || undefined,
      });
      haptic.notification('success');
      toast.success('Замовлення створено');
      onCheckedOut(resp.chat_id);
    } catch (err) {
      logger.warn('cart checkout failed', err);
      toast.error('Не вдалось оформити замовлення');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      title="Оформити замовлення"
      onClose={busy ? () => undefined : onClose}
      actions={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Скасувати
          </Button>
          <Button onClick={() => void submit()} disabled={busy}>
            {busy ? 'Надсилаю…' : 'Замовити'}
          </Button>
        </>
      }
    >
      <p style={{ margin: '0 0 12px', color: 'var(--fg-muted)', fontSize: 13 }}>
        {cart.item_count} позицій · {Number(cart.total_eur).toFixed(2)} €
      </p>

      <fieldset
        style={{
          border: 0,
          padding: 0,
          margin: '0 0 12px',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}
      >
        <legend style={{ fontSize: 13, color: 'var(--fg-muted)', marginBottom: 6 }}>
          Як отримати
        </legend>
        {allModes.map((m) => (
          <label
            key={m}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            <input
              type="radio"
              name="delivery-mode"
              value={m}
              checked={mode === m}
              onChange={() => setMode(m)}
            />
            {MODE_LABEL[m]}
          </label>
        ))}
      </fieldset>

      {needsAddress ? (
        <Textarea
          label="Адреса доставки"
          value={address}
          error={addressError}
          onChange={(e) => {
            setAddress(e.target.value.slice(0, MAX_TEXT));
            setAddressError(undefined);
          }}
          rows={2}
          placeholder="Місто, вулиця, поверх…"
        />
      ) : null}

      <Textarea
        label={`Коментар (необовʼязково) · ${comment.length}/${MAX_TEXT}`}
        value={comment}
        onChange={(e) => setComment(e.target.value.slice(0, MAX_TEXT))}
        rows={3}
        placeholder="Щось, що варто знати майстру?"
      />
    </Modal>
  );
}
